import { motion } from "framer-motion";
import { ArrowUpRight, CircuitBoard, PackageCheck, Check, Plus } from "lucide-react";
import { SectionHead, Reveal, EASE } from "./Reveal";
import { useContent } from "@/context/ContentContext";
import { VEHICLES, formatEur } from "@/lib/site";

function Chip({ children }) {
  return (
    <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-400 border border-zinc-800 rounded-full px-3 py-1">
      {children}
    </span>
  );
}

function PriceTag({ children, rose }) {
  return <span className={`font-mono text-sm font-bold ${rose ? "text-rose-400" : "text-blue-400"}`}>{children}</span>;
}

function Card({ testid, className, children, delay = 0 }) {
  return (
    <motion.article
      data-testid={testid}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
      className={`group relative overflow-hidden rounded-2xl border border-[#27272A] bg-[#121215] transition-colors hover:border-blue-500/40 h-full ${className}`}
    >
      {children}
    </motion.article>
  );
}

function CtaRow({ label = "Termin anfragen", onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors"
    >
      {label}
      <ArrowUpRight className="h-4 w-4" />
    </button>
  );
}

function ClassPriceStrip({ pkg }) {
  const values = pkg.prices || {};
  if (!Object.keys(values).length) return null;
  return (
    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 rounded-xl border border-zinc-800/80 bg-[#0C0C0E] px-4 py-2.5">
      {VEHICLES.map((v) => (
        <span key={v.id} className="font-mono text-[11px] text-zinc-400">
          <span className="text-zinc-500">{v.code}</span>{" "}
          <span className="font-bold text-zinc-200">{formatEur(values[v.id])}</span>
        </span>
      ))}
    </div>
  );
}

export default function Services({ onBook }) {
  const c = useContent();
  const t = c.texts;
  const featured = c.packages.find((p) => p.featured) || c.packages[0];
  const others = c.packages.filter((p) => p !== featured);
  const other = others[0];
  const extraPackages = others.slice(1);
  const obd = c.addons.find((a) => a.id === "obd" && a.active !== false);

  const addonName = (id) => c.addons.find((a) => a.id === id)?.name;
  const optionalFor = (pkg) =>
    (pkg.optional_addon_ids || [])
      .map((id) => c.addons.find((a) => a.id === id && a.active !== false))
      .filter(Boolean);

  return (
    <section id="leistungen" data-testid="services-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHead eyebrow={t.services_eyebrow} title={t.services_title} sub={t.services_sub} />
        </div>

        {featured && (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-5">
            <Card testid="service-card-aufbereitung" className="lg:col-span-7 min-h-[380px]" delay={0}>
              <div className="grid h-full grid-cols-1 sm:grid-cols-2">
                <div className="flex flex-col justify-between p-7 sm:p-8">
                  <div>
                    <div className="flex items-center justify-between">
                      <Chip>{featured.name}</Chip>
                      <PriceTag>ab {formatEur(Math.min(...Object.values(featured.prices || { s: 0 })))}</PriceTag>
                    </div>
                    <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold text-zinc-100">{featured.name}</h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{featured.desc}</p>
                    {featured.includes?.length > 0 && (
                      <div className="mt-5">
                        <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Das ist enthalten</p>
                        <ul className="flex flex-col gap-1.5">
                          {featured.includes.map((inc) => (
                            <li key={inc} className="flex items-center gap-2 text-sm text-zinc-200">
                              <Check className="h-4 w-4 text-blue-400 shrink-0" /> {inc}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {optionalFor(featured).length > 0 && (
                      <div className="mt-4">
                        <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Optional hinzubuchbar</p>
                        <div className="flex flex-wrap gap-2">
                          {optionalFor(featured).map((a) => (
                            <Chip key={a.id}>
                              <span className="inline-flex items-center gap-1">
                                <Plus className="h-3 w-3 text-blue-400" /> {a.name}
                              </span>
                            </Chip>
                          ))}
                        </div>
                      </div>
                    )}
                    <ClassPriceStrip pkg={featured} />
                    {featured.note ? <p className="mt-3 text-xs leading-relaxed text-zinc-500">{featured.note}</p> : null}
                  </div>
                  <div className="mt-8">
                    <CtaRow label="Dieses Paket anfragen" testid="service-cta-aufbereitung" onClick={() => onBook({ service: featured.name, notes: `Paket: ${featured.name}` })} />
                  </div>
                </div>
                <div className="relative min-h-[220px] sm:min-h-0 overflow-hidden">
                  <img
                    src={featured.image_url}
                    alt={`${featured.name} – Fahrzeuginnenraum`}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121215]/70 via-transparent to-transparent sm:bg-gradient-to-r sm:from-[#121215] sm:via-transparent" />
                </div>
              </div>
            </Card>

            {other && (
              <Card testid="service-card-reinigung" className="lg:col-span-5" delay={0.08}>
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={other.image_url}
                    alt={`${other.name} – Fahrzeuginnenraum`}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121215] via-transparent to-transparent" />
                </div>
                <div className="p-7 sm:p-8 pt-2">
                  <div className="flex items-center justify-between">
                    <Chip>{other.name}</Chip>
                    <PriceTag>ab {formatEur(Math.min(...Object.values(other.prices || { s: 0 })))}</PriceTag>
                  </div>
                  <h3 className="mt-4 font-display text-xl sm:text-2xl font-semibold text-zinc-100">{other.name}</h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{other.desc}</p>
                  {optionalFor(other).length > 0 && (
                    <div className="mt-4">
                      <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Optional hinzubuchbar</p>
                      <div className="flex flex-wrap gap-2">
                        {optionalFor(other).map((a) => (
                          <Chip key={a.id}>
                            <span className="inline-flex items-center gap-1">
                              <Plus className="h-3 w-3 text-blue-400" /> {a.name}
                            </span>
                          </Chip>
                        ))}
                      </div>
                    </div>
                  )}
                  <ClassPriceStrip pkg={other} />
                  {other.note ? <p className="mt-3 text-xs leading-relaxed text-zinc-500">{other.note}</p> : null}
                  <div className="mt-6">
                    <CtaRow label="Dieses Paket anfragen" testid="service-cta-reinigung" onClick={() => onBook({ service: other.name, notes: `Paket: ${other.name}` })} />
                  </div>
                </div>
              </Card>
            )}

            <Card testid="service-card-geruch" className="lg:col-span-5" delay={0.12}>
              <div className="flex flex-col justify-between h-full p-7 sm:p-8">
                <div>
                  <div className="flex items-center justify-between">
                    <PackageCheck className="h-6 w-6 text-blue-400" />
                    <PriceTag>nach Absprache</PriceTag>
                  </div>
                  <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold text-zinc-100">
                    {t.custom_package_title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{t.custom_package_desc}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {(t.custom_package_tags || "").split(",").filter(Boolean).map((tag) => (
                      <Chip key={tag.trim()}>{tag.trim()}</Chip>
                    ))}
                  </div>
                </div>
                <div className="mt-8">
                  <CtaRow label="Paket anfragen" testid="service-cta-custom" onClick={() => onBook({ service: t.custom_package_title, notes: `Paket: ${t.custom_package_title}` })} />
                </div>
              </div>
            </Card>

            {obd ? (
              <Card testid="service-card-obd" className="lg:col-span-7" delay={0.16}>
                <div className="relative flex flex-col justify-between h-full p-7 sm:p-8">
                  <span className="absolute top-6 right-6 rounded-full bg-rose-500/10 border border-rose-500/30 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-rose-400">
                    Zusatzleistung
                  </span>
                  <div>
                    <CircuitBoard className="h-6 w-6 text-rose-400" />
                    <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold text-zinc-100">{obd.name}</h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-lg">{obd.desc}</p>
                  </div>
                  <div className="mt-8 flex items-center justify-between">
                    <PriceTag rose>{formatEur(obd.price)}</PriceTag>
                    <CtaRow label="Diagnose anfragen" testid="service-cta-obd" onClick={() => onBook({ service: obd.name, notes: `OBD-Diagnose (${formatEur(obd.price)})` })} />
                  </div>
                </div>
              </Card>
            ) : null}

            {extraPackages.map((p, i) => (
              <Card key={p.id} testid={`service-card-${p.id}`} className="lg:col-span-6" delay={0.1 + i * 0.06}>
                <div className="flex flex-col justify-between h-full p-7 sm:p-8">
                  <div>
                    <div className="flex items-center justify-between">
                      <Chip>{p.name}</Chip>
                      <PriceTag>ab {formatEur(Math.min(...Object.values(p.prices || { s: 0 })))}</PriceTag>
                    </div>
                    <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold text-zinc-100">{p.name}</h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
                    <ClassPriceStrip pkg={p} />
                  </div>
                  <div className="mt-8">
                    <CtaRow label="Dieses Paket anfragen" onClick={() => onBook({ service: p.name, notes: `Paket: ${p.name}` })} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
